import { useState } from "react";
import { useBeforeUnload, useNavigate } from "react-router-dom";
import posthog from "posthog-js";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../../../../components/ui/dialog";
import { Progress } from "../../../../components/ui/progress";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../../../components/ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../../../../components/ui/table";
import { formatCurrency } from "../../../../lib/utils/utils-functions";
import { useCentralStore } from "../../../../store/Central";
import { useRemindersStore } from "../../../../store/Reminders";
import { usePaymentReminderPdf } from "../hooks/usePaymentReminderPdf";
import {
	MahnwesenNavigationState,
	PaymentReminderCandidate,
} from "../payment-reminder.types";
import { MAHNWESEN_HISTORY_PATH } from "../utils/mahnwesen-route";
import { getReminderStageFee } from "../utils/mahnwesen-timeline";
import { OPEN_EPSILON } from "../utils/payment-reminder-document";
import { formatReminderDate } from "../utils/reminder-placeholders";
import {
	REMINDER_TEMPLATE_LABELS,
	getReminderTemplateType,
} from "../utils/reminder-stage";
import { getReminderStageLabel } from "@/lib/utils/reminder-stage-label";

interface CreateRemindersDialogProps {
	open: boolean;
	candidates: PaymentReminderCandidate[];
	ignoreSchedule: boolean;
	onClose: () => void;
	onCompleted: () => void;
}

interface RunFailure {
	docNumber: string;
	message: string;
}

type Phase = "preview" | "running" | "done";

export const CreateRemindersDialog: React.FC<CreateRemindersDialogProps> = ({
	open,
	candidates,
	ignoreSchedule,
	onClose,
	onCompleted,
}) => {
	const navigate = useNavigate();
	const { preflightReminderSlips, createReminder } = usePaymentReminderPdf();
	const [phase, setPhase] = useState<Phase>("preview");
	const [current, setCurrent] = useState(0);
	const [failures, setFailures] = useState<RunFailure[]>([]);
	const [createdReminderIds, setCreatedReminderIds] = useState<number[]>([]);
	const [stageOverrides, setStageOverrides] = useState<
		Record<number, number>
	>({});
	const feeAmounts = useCentralStore(
		(state) => state.organization?.reminder_fee_amounts ?? null
	);
	const maxStage = useCentralStore(
		(state) => state.organization?.mahnung_max_count ?? 1
	);
	const isSingle = candidates.length === 1;
	const today = new Date().toISOString().split("T")[0];
	const stageOf = (candidate: PaymentReminderCandidate) =>
		stageOverrides[candidate.invoice_id] ?? candidate.next_stage;
	const feeOf = (candidate: PaymentReminderCandidate) =>
		getReminderStageFee(feeAmounts, stageOf(candidate));
	const totalFees = candidates.reduce(
		(sum, candidate) => sum + feeOf(candidate),
		0
	);
	const earlyCandidate = candidates.find(
		(candidate) => ignoreSchedule && candidate.due_from > today
	);

	useBeforeUnload((event) => {
		if (phase !== "running") return;
		event.preventDefault();
		event.returnValue = "Mahnungen werden erstellt. Wirklich verlassen?";
	});

	const handleClose = () => {
		if (phase === "running") return;
		setPhase("preview");
		setCurrent(0);
		setFailures([]);
		setCreatedReminderIds([]);
		setStageOverrides({});
		onClose();
		if (phase !== "done") return;
		onCompleted();
		if (createdReminderIds.length === 0) return;
		const state: MahnwesenNavigationState = { createdReminderIds };
		navigate(MAHNWESEN_HISTORY_PATH, { state });
	};

	const handleCreate = async () => {
		setPhase("running");
		const runFailures: RunFailure[] = [];
		const runCreatedIds: number[] = [];
		const slipProblems = await preflightReminderSlips(
			candidates.map((candidate) => ({
				invoiceId: candidate.invoice_id,
				docNumber: candidate.doc_number,
				clientId: candidate.client_id,
				currency: candidate.currency,
				openAmount: candidate.open_amount,
				needsFeeSlip:
					candidate.open_fee_amount > OPEN_EPSILON ||
					feeOf(candidate) > 0,
			}))
		);
		for (let index = 0; index < candidates.length; index++) {
			setCurrent(index + 1);
			const candidate = candidates[index];
			const slipProblem = slipProblems.get(candidate.invoice_id);
			if (slipProblem) {
				runFailures.push({
					docNumber: candidate.doc_number,
					message: slipProblem,
				});
				continue;
			}
			const { reminderId, error } = await createReminder(
				candidate.invoice_id,
				ignoreSchedule,
				stageOf(candidate)
			);
			if (reminderId !== null) runCreatedIds.push(reminderId);
			if (error) {
				runFailures.push({
					docNumber: candidate.doc_number,
					message: error,
				});
			}
		}
		setFailures(runFailures);
		setCreatedReminderIds(runCreatedIds);
		posthog.capture("payment_reminders_created", {
			count: candidates.length - runFailures.length,
			failed: runFailures.length,
			source: ignoreSchedule ? "offene_posten" : "mahnwesen",
		});
		useRemindersStore.getState().fetchDueCount();
		setPhase("done");
	};

	const title = isSingle
		? getReminderStageLabel(stageOf(candidates[0])) + " erstellen"
		: `${candidates.length} Mahnungen erstellen`;

	return (
		<Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
			<DialogContent
				className="sm:max-w-3xl"
				data-testid="create-reminders-dialog"
				aria-label={title}
			>
				<DialogHeader>
					<DialogTitle>{title}</DialogTitle>
					<DialogDescription>
						Jede Mahnung wird als PDF abgelegt, Gebühren als eigene
						Gebührenrechnung gebucht.
					</DialogDescription>
				</DialogHeader>

				{phase === "preview" && (
					<>
						{earlyCandidate && (
							<div
								role="note"
								data-testid="reminder-schedule-hint"
								className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
							>
								<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
								<span>
									Wartefrist noch nicht abgelaufen — regulär
									fällig ab{" "}
									{formatReminderDate(
										earlyCandidate.due_from
									)}
								</span>
							</div>
						)}
						<div className="max-h-[50vh] overflow-auto">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Rechnung</TableHead>
										<TableHead>Empfänger</TableHead>
										<TableHead>Stufe</TableHead>
										<TableHead>Vorlage</TableHead>
										<TableHead className="text-right">
											Gebühr
										</TableHead>
										<TableHead className="text-right">
											Neues Total
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{candidates.map((candidate) => (
										<TableRow
											key={candidate.invoice_id}
											data-testid={`reminder-preview-row-${candidate.doc_number}`}
										>
											<TableCell>
												{candidate.doc_number}
											</TableCell>
											<TableCell>
												{candidate.recipient_name}
											</TableCell>
											<TableCell>
												<Select
													value={String(
														stageOf(candidate)
													)}
													onValueChange={(value) =>
														setStageOverrides(
															(current) => ({
																...current,
																[candidate.invoice_id]:
																	Number(
																		value
																	),
															})
														)
													}
												>
													<SelectTrigger
														className="h-8 w-44"
														aria-label={`Mahnstufe für Rechnung ${candidate.doc_number}`}
														data-testid={`reminder-preview-stage-${candidate.doc_number}`}
													>
														<SelectValue />
													</SelectTrigger>
													<SelectContent>
														{Array.from(
															{
																length:
																	maxStage -
																	candidate.next_stage +
																	1,
															},
															(_, index) =>
																candidate.next_stage +
																index
														).map((stage) => (
															<SelectItem
																key={stage}
																value={String(
																	stage
																)}
															>
																{getReminderStageLabel(
																	stage
																)}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
											</TableCell>
											<TableCell>
												{
													REMINDER_TEMPLATE_LABELS[
														getReminderTemplateType(
															stageOf(candidate),
															maxStage
														)
													]
												}
											</TableCell>
											<TableCell className="text-right">
												{formatCurrency(
													feeOf(candidate),
													candidate.currency
												)}
											</TableCell>
											<TableCell className="text-right">
												{formatCurrency(
													candidate.open_amount +
														candidate.open_fee_amount +
														feeOf(candidate),
													candidate.currency
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
						<p className="text-right text-sm font-medium">
							Mahngebühren gesamt: {formatCurrency(totalFees)}
						</p>
					</>
				)}

				{phase === "running" && (
					<div
						className="flex flex-col gap-3 py-4"
						data-testid="create-reminders-progress"
					>
						<div className="flex items-center gap-2 text-sm">
							<Loader2 className="h-4 w-4 animate-spin" />
							Erstelle {current} von {candidates.length}
						</div>
						<Progress value={(current / candidates.length) * 100} />
					</div>
				)}

				{phase === "done" && (
					<div
						className="flex flex-col gap-3 py-2"
						data-testid="create-reminders-result"
					>
						<div className="flex items-center gap-2 text-sm">
							<CheckCircle2 className="h-4 w-4 text-green-600" />
							{candidates.length - failures.length} von{" "}
							{candidates.length} Mahnungen erstellt
						</div>
						{failures.length > 0 && (
							<ul className="flex flex-col gap-1 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
								{failures.map((failure) => (
									<li key={failure.docNumber}>
										<span className="font-medium">
											{failure.docNumber}:
										</span>{" "}
										{failure.message}
									</li>
								))}
							</ul>
						)}
					</div>
				)}

				<DialogFooter>
					{phase === "preview" && (
						<>
							<Button
								variant="outline"
								onClick={handleClose}
								aria-label="Abbrechen"
								data-testid="cancel-create-reminders-button"
							>
								Abbrechen
							</Button>
							<Button
								onClick={handleCreate}
								aria-label="Mahnungen jetzt erstellen"
								data-testid="confirm-create-reminders-button"
							>
								{isSingle ? "Erstellen" : "Alle erstellen"}
							</Button>
						</>
					)}
					{phase === "done" && (
						<Button
							onClick={handleClose}
							aria-label={
								createdReminderIds.length > 0
									? "Im Mahnverlauf anzeigen"
									: "Schliessen"
							}
							data-testid="close-create-reminders-button"
						>
							{createdReminderIds.length > 0
								? "Im Mahnverlauf anzeigen"
								: "Schliessen"}
						</Button>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
